import { z } from 'zod'

import type { PremiumInterval, PriceData } from '@flows-ui/services/QuoteDataContext.types'

import { getSinglePriceWithInterval } from './getSinglePriceWithInterval'
import { buildOpprefTrackingParams } from './partnerTracking'

const trackingFlowValuesSchema = z.object({
  first_name: z.string().optional(),
  email: z.string().optional(),
  phone_number: z.string().optional(),
  premium_interval: z.enum(['P1M', 'P1Y']).optional(),
  coupon_code: z.string().optional(),
  coupon_amount: z.number().optional(),
  products: z
    .record(
      z.object({
        configuration_parameters: z
          .object({
            tariff_line: z.string().optional(),
          })
          .passthrough()
          .optional(),
      })
    )
    .optional(),
})

export type TrackingFlowValues = z.infer<typeof trackingFlowValuesSchema>

export const extractTrackingFlowValues = (raw: unknown): TrackingFlowValues => {
  const parsed = trackingFlowValuesSchema.safeParse(raw)
  return parsed.success ? parsed.data : {}
}

export const getTariffLine = (
  flowValues: TrackingFlowValues,
  productKey: string
): string | undefined => flowValues.products?.[productKey]?.configuration_parameters?.tariff_line

export const getConfigurationParameters = (
  flowValues: TrackingFlowValues,
  productKey: string
): Record<string, unknown> => flowValues.products?.[productKey]?.configuration_parameters ?? {}

export const getPremiumIntervalFromValues = (flowValues: TrackingFlowValues): PremiumInterval =>
  flowValues.premium_interval === 'P1Y' ? 'P1Y' : 'P1M'

export const permanentDiscountTrackingSchema = z.object({
  shown: z.boolean(),
  amount: z.number().nullable(),
  token: z.string().nullable(),
  revoked: z.boolean(),
})

export type PermanentDiscountTracking = z.infer<typeof permanentDiscountTrackingSchema>

export const defaultPermanentDiscountTracking: PermanentDiscountTracking = {
  shown: false,
  amount: null,
  token: null,
  revoked: false,
}

export type CouponTracking = { code: string | undefined; amount: number | undefined }

export const buildCouponTrackingParams = (coupon: CouponTracking | null | undefined) => ({
  ...(coupon?.code ? { coupon_code: coupon.code } : {}),
  ...(typeof coupon?.amount === 'number' ? { coupon_amount: coupon.amount } : {}),
})

export const getCouponTracking = (flowValues: unknown): CouponTracking => {
  const tracking = extractTrackingFlowValues(flowValues)
  return { code: tracking.coupon_code, amount: tracking.coupon_amount }
}

export const getYearlyPriceEstimate = (price: string, interval: PremiumInterval): string =>
  interval === 'P1Y' ? price : (Number.parseFloat(price) * 12).toString()

export const getMonthlyGrossPremium = ({
  priceData,
  productKey,
  tariffLine,
}: {
  priceData: PriceData | undefined
  productKey: string
  tariffLine: string | undefined
}): string | undefined => {
  const monthlyGrossPremium = Number.parseFloat(
    getSinglePriceWithInterval({ premiumInterval: 'P1M', priceData, productKey, tariffLine })
  )

  if (Number.isNaN(monthlyGrossPremium) || monthlyGrossPremium === 0) {
    return undefined
  }

  return monthlyGrossPremium.toFixed(2)
}

const CURRENCY_BY_MARKET: Record<string, string> = {
  de: 'EUR',
  at: 'EUR',
  fr: 'EUR',
  uk: 'GBP',
  gb: 'GBP',
}

export const getCurrencyForMarket = (market: string): string =>
  CURRENCY_BY_MARKET[market.toLowerCase()] ?? 'EUR'

type ProductTrackingInfo = {
  product_id: string
  name: string
  variant: string | undefined
  extensions: string[]
  configuration_data: Record<string, unknown>
  permanent_discount: PermanentDiscountTracking
  price: string
  quantity: number
}

export type BuildOrderCompletedPayloadInput = {
  flowSessionId: string
  flowName?: string
  market: string
  email: string | undefined
  phoneNumber: string | undefined
  premiumInterval: PremiumInterval
  priceData: PriceData | undefined
  productKey: string
  variant: string | undefined
  extensions: string[]
  configurationData: Record<string, unknown>
  permanentDiscount?: PermanentDiscountTracking
  coupon?: CouponTracking | null
  ltv?: number
  oppref?: string
}

export const buildOrderCompletedPayload = (input: BuildOrderCompletedPayloadInput) => {
  const monthlyGrossPremium = getMonthlyGrossPremium({
    priceData: input.priceData,
    productKey: input.productKey,
    tariffLine: input.variant,
  })
  const product: ProductTrackingInfo = {
    product_id: input.productKey,
    name: input.productKey,
    variant: input.variant,
    extensions: input.extensions,
    configuration_data: input.configurationData,
    permanent_discount: input.permanentDiscount ?? defaultPermanentDiscountTracking,
    price: monthlyGrossPremium ?? '0',
    quantity: 1,
  }
  const fallbackRevenue = Number.parseFloat(
    getSinglePriceWithInterval({
      premiumInterval: input.premiumInterval,
      priceData: input.priceData,
      productKey: input.productKey,
      tariffLine: input.variant,
    })
  )
  return {
    order_id: input.flowSessionId,
    ...(input.flowName ? { flowName: input.flowName } : {}),
    currency: getCurrencyForMarket(input.market),
    market: input.market,
    email: input.email,
    phone_number: input.phoneNumber,
    products: [product],
    revenue: input.ltv ?? fallbackRevenue,
    ...(monthlyGrossPremium !== undefined && { monthly_gross_premium: monthlyGrossPremium }),
    ...buildCouponTrackingParams(input.coupon),
    ...buildOpprefTrackingParams(input.oppref),
  }
}

export type BuildProductAddedPayloadInput = {
  market: string
  premiumInterval: PremiumInterval
  priceData: PriceData | undefined
  productKey: string
  variant: string | undefined
  coupon?: CouponTracking | null
  oppref?: string
}

export const buildProductAddedPayload = (input: BuildProductAddedPayloadInput) => {
  const price = getSinglePriceWithInterval({
    premiumInterval: input.premiumInterval,
    priceData: input.priceData,
    productKey: input.productKey,
    tariffLine: input.variant,
  })
  const monthlyGrossPremium = getMonthlyGrossPremium({
    priceData: input.priceData,
    productKey: input.productKey,
    tariffLine: input.variant,
  })
  return {
    price: getYearlyPriceEstimate(price, input.premiumInterval),
    product_id: input.productKey,
    currency: getCurrencyForMarket(input.market),
    ...(monthlyGrossPremium !== undefined && { monthly_gross_premium: monthlyGrossPremium }),
    ...buildCouponTrackingParams(input.coupon),
    ...buildOpprefTrackingParams(input.oppref),
  }
}

export type BuildAddPaymentInfoPayloadInput = {
  market: string
  premiumInterval: PremiumInterval
  priceData: PriceData | undefined
  productKey: string
  variant: string | undefined
  paymentType: string
  email: string | undefined
  phoneNumber: string | undefined
  oppref?: string
}

const EVENTS_CATEGORIES_CHECKOUT = 'Checkout'
const EVENTS_LABELS_PAYMENT = 'Payment-Methods'

export const buildAddPaymentInfoPayload = (input: BuildAddPaymentInfoPayloadInput) => {
  const price = getSinglePriceWithInterval({
    premiumInterval: input.premiumInterval,
    priceData: input.priceData,
    productKey: input.productKey,
    tariffLine: input.variant,
  })
  const monthlyGrossPremium = getMonthlyGrossPremium({
    priceData: input.priceData,
    productKey: input.productKey,
    tariffLine: input.variant,
  })
  return {
    price,
    category: EVENTS_CATEGORIES_CHECKOUT,
    label: EVENTS_LABELS_PAYMENT,
    value: input.paymentType,
    currency: getCurrencyForMarket(input.market),
    interval: input.premiumInterval,
    email: input.email,
    phone_number: input.phoneNumber,
    ...(monthlyGrossPremium !== undefined && { monthly_gross_premium: monthlyGrossPremium }),
    ...buildOpprefTrackingParams(input.oppref),
  }
}
